import { Box, CircularProgress, FormControl, Grid, IconButton, InputLabel, NativeSelect, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { EditTwoTone } from '@mui/icons-material';
import { cellStyle, evenRowStyle, headerCellStyle, oddRowStyle, rowStyle } from '../../../utility/CustomStyle';
import configServ from '../../../services/config';
import { useSelector } from 'react-redux';
import useDebounce from '../../hooks/useDebounce';
import SearchBox from '../../Search/SearchBox';


// FUNCTION
const QuestionsTable = ({ isChanged, handleEdit }) => {
    //#region code
    const { company_id } = useSelector((state) => state.GlobalVariables);
    const rowsPerPageOptions = [10, 20, 50, 100];
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [count, setCount] = useState(0);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("id");
    const [questionList, setQuestionList] = useState([]);
    const [searchBy, setSearchBy] = useState("question");
    const [search, setSearch] = useState(null);
    const debounceSearch = useDebounce(search, 1000);

    const headers = [
        { id: "question", label: "QUESTION" },
        { id: "description", label: "DESCRIPTION" },
        { id: "status", label: "STATUS" },
    ];

    const fetchQuestionList = async () => {
        try {
            setLoading(true);
            const dataToSend = {
                company_id: company_id,
                page: page,
                per_page: rowsPerPage,
                search: search,
                search_by: searchBy
            }
            const res = await configServ.getQuestions(dataToSend);
            if (res.status === 200) {
                setQuestionList(res.data);
                setCount(res.total);
            }
            else {
                setQuestionList([]);
                setCount(0);
            }
        }
        catch (e) {
            console.log(e);
        }
        finally {
            setLoading(false);
        }
    }

    const callAsyncQuestionList = async () => {
        await fetchQuestionList();
    }

    useEffect(() => {
        callAsyncQuestionList();
    }, [page]);

    useEffect(() => {
        if (page === 0) {
            callAsyncQuestionList();
        }
        else {
            setPage(0);
        }
    }, [isChanged, rowsPerPage, debounceSearch]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchBy = (event) => {
        try {
            setSearchBy(event.target.value);
        }
        catch (error) {
            console.log(error);
        }
    };
    //#endregion

    return (
        <>
            <Box width={'100%'} sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
                <Grid container alignItems="flex-end" justifyContent="flex-start" spacing={1}>
                    <Grid container item xs={12} md={6} justifyContent={"flex-start"}>
                        <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
                            Question List: {count}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ minWidth: 120 }}>
                            <FormControl fullWidth>
                                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                    Search By
                                </InputLabel>
                                <NativeSelect
                                    value={searchBy}
                                    onChange={handleSearchBy}
                                    inputProps={{
                                        name: "searchBy",
                                        id: "uncontrolled-native",
                                    }}
                                >
                                    <option value={"question"}>Question</option>
                                </NativeSelect>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SearchBox search={search} setSearch={setSearch} />
                    </Grid>
                </Grid>
            </Box>

            <Box width={'100%'} sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                                {headers.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sortDirection={orderBy === column.id ? order : false}
                                        sx={headerCellStyle}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : "asc"}
                                            onClick={() => handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell sx={headerCellStyle}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading === false ? (
                                questionList.length > 0 ? (
                                    questionList.map((row, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            sx={{
                                                ...rowStyle,
                                                ...(rowIndex % 2 === 0
                                                    ? evenRowStyle
                                                    : oddRowStyle),
                                            }}
                                        >
                                            <TableCell sx={cellStyle}>
                                                <Typography variant="body1">
                                                    {page * rowsPerPage + rowIndex + 1}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {row.question}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {row.description || ""}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {
                                                    Boolean(row.status) === true ?
                                                        <CheckCircleIcon color="success" />
                                                        :
                                                        <CancelIcon color="error" />
                                                }
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                <Stack direction={"row"}>
                                                    <Tooltip title="Edit" placement="top">
                                                        <IconButton sx={{ cursor: "pointer" }}>
                                                            <EditTwoTone
                                                                color='primary'
                                                                onClick={() => handleEdit(row)}
                                                                sx={{ cursor: "pointer" }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>                                        
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={12}>
                                            <Box textAlign={"center"}>
                                                <Typography variant="body1" color={"red"}>
                                                    No data found.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                                        <Box>
                                            <CircularProgress />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            mt: 'auto', // Ensure the pagination stays at the bottom
                        }}
                    />
                </TableContainer>
            </Box>
        </>
    )
}

export default memo(QuestionsTable);
